/**
 * URL Parser
 * Parse URL parameters and initialize state from URL
 * This is the entry point for the search - handles URL parsing, geocoding, and triggering search
 */

import { state, loadFromLocalStorage, saveToLocalStorage, saveLiveChoice, loadLiveChoice } from '../core/state.js';
import { elements } from '../core/elements.js';
import { decodeTier } from './tier-encoding.js';
import { replaceNextURLUpdate, SEARCH_PARAM_KEYS } from './url-updater.js';
import { normalizeBrands } from '../utils/brand-utils.js';
import { mergeFacetKeys } from '../utils/facet-utils.js';
import { toggleSkeleton } from '../ui/skeleton.js';
import { searchCampByTitle } from '../algolia/camp-title-search.js';
import { performDualSearch } from '../algolia/dual-search.js';
import { runDecisionFlow } from '../decision-engine/index.js';
import { isZipCode, isLocationEffectivelySame, hasSpellingMistakes } from '../decision-engine/location-comparison.js';
import { readUserGeoFromCache } from '../decision-engine/user-geo.js';
import { normalizeCompareStr } from '../utils/string-utils.js';
import { getDateRangeTimestamps, campMatchesDateFilter } from '../filters/date-filter.js';
import { buildGenderFilterExpression, getSelectedGender } from '../filters/gender-filter.js';
import { getCampStartDate } from '../utils/date-utils.js';
import { hideFallbackBanner } from '../ui/banner-manager.js';

// Will be injected by index.js
let performSearch = null;
let initializeFormComponents = null;

/**
 * Set dependencies (dependency injection)
 */
export function setDependencies(deps) {
  performSearch = deps.performSearch;
  initializeFormComponents = deps.initializeFormComponents;
}

/**
 * Apply coordinates from geolocation cache (IP-based)
 * Used as fallback when no location is entered or geocoding fails
 */
export function applyCoordinatesFromCache() {
  if (state.coordinates) {
    console.log('📍 Coordinates already set:', state.coordinates);
    return;
  }

  const defaults = window.CampsUtils?.defaults || {};
  const cacheKey = defaults.cacheKey || 'campsNearYou_geoLocation_v5';

  // Check if we have geolocation data to determine US/non-US
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const cacheData = JSON.parse(cached);

      // PRIORITY 1: Use last successful search location (coords) if available
      // This ensures that when location is unrecognized, we show results near the last successful search
      const coords = cacheData.coords;
      if (coords && typeof coords.lat === 'number' && typeof coords.lng === 'number') {
        if (coords.isUS === false) {
          // Non-US coords - use San Rafael for sorting
          state.coordinates = {
            lat: defaults.fallbackCoords.lat,
            lng: defaults.fallbackCoords.lng,
          };
          console.log('✅ Applied San Rafael (last search was non-US)');
          return;
        }
        // US coords - use last successful search location
        state.coordinates = { lat: coords.lat, lng: coords.lng };
        console.log('✅ Applied last successful search location from cache.coords');
        return;
      }

      // PRIORITY 2: Fallback to IP-based location if no search history
      if (cacheData.realLocationData) {
        if (cacheData.realLocationData.isUS === true) {
          // US user - use their real IP coordinates
          if (cacheData.realLocationData.lat && cacheData.realLocationData.lng) {
            state.coordinates = {
              lat: cacheData.realLocationData.lat,
              lng: cacheData.realLocationData.lng,
            };
            console.log('✅ Applied US user IP coordinates (no search history)');
            return;
          }
        } else {
          // Non-US user - use San Rafael coordinates for sorting
          state.coordinates = {
            lat: defaults.fallbackCoords.lat,
            lng: defaults.fallbackCoords.lng,
          };
          console.log('✅ Applied San Rafael coordinates for non-US IP (no search history)');
          return;
        }
      }
    }
  } catch (e) {
    console.warn('⚠️ Error reading geolocation cache:', e);
  }

  // Fallback: if no geolocation data, use San Rafael coordinates
  state.coordinates = {
    lat: defaults.fallbackCoords.lat,
    lng: defaults.fallbackCoords.lng,
  };
  console.log('⚠️ No geolocation data found, using San Rafael coordinates');
}

/**
 * Check if query looks like a camp name using heuristics
 * @param {string} query - Search query
 * @returns {boolean} - True if query looks like camp name
 */
function isCampName(query) {
  if (!query) return false;

  const queryLower = query.toLowerCase().trim();
  const wordCount = query.trim().split(/\s+/).length;

  // Camp-related keywords
  const campKeywords = [
    'camp',
    'school',
    'academy',
    'clinic',
    'training',
    'tournament',
    'showcase',
    'elite',
    'select',
    'nike',
    'behn',
    'peak performance',
    'us sports',
  ];

  const hasCampKeyword = campKeywords.some((keyword) => queryLower.includes(keyword));

  // Exception: "Camp Verde" or "Camp <single word>" is likely a city name
  const isCityWithCampName = /^camp\s+\w+$/i.test(query.trim());

  // Possessive form (coach's camp) or multiple words suggesting camp name
  const hasApostrophe = query.includes("'") || query.includes("'");

  const isCampName =
    query.length > 35 || // Long query
    (hasCampKeyword && !isCityWithCampName) || // Camp keywords (except "Camp CityName")
    wordCount >= 6 || // Many words (6+)
    (hasApostrophe && wordCount >= 3); // Possessive + multiple words

  return isCampName;
}

/**
 * Initialize state from URL parameters
 * Main entry point - parses URL, handles geocoding, and triggers search
 * @param {boolean} isPopState - Browser back/forward event
 */
export async function initFromURL(isPopState = false) {
  // The URL sync that follows this init must not add a history entry
  replaceNextURLUpdate();
  const params = new URLSearchParams(window.location.search);
  // Only search keys count: ad/tracking params alone (gclid, utm_*, fbclid...)
  // must not block the saved search or the IP location.
  const hasURLParams = SEARCH_PARAM_KEYS.some((key) => params.has(key));

  // Reset per init: recomputed below once state.zip is known. True = the search
  // location is the user's OWN detected location (empty/near-me OR the IP city),
  // which must center on precise IP coords, not the static cities.json centroid.
  state.isDefaultIpLocation = false;

  // Default live state from CP setting (searchResultsPage.enableLiveByDefault):
  // ON → live-only (true); OFF → show all (null). Missing config → true (backwards compatible).
  // The user's choice in this session wins over the CP default (see saveLiveChoice).
  const liveChoice = loadLiveChoice();
  const defaultLive =
    liveChoice !== undefined
      ? liveChoice
      : window.CampsUtils?.defaults?.enableLiveByDefault === false ? null : true;

  if (hasURLParams) {
    // Load from URL parameters
    state.zip = params.get('q') || '';
    // q may be the user's own detected city (e.g. prefilled by the search box).
    // In that case center on the real IP coords — same as an empty "near me".
    state.isDefaultIpLocation =
      window.CampsUtils?.isOwnDetectedLocation?.(state.zip) ?? false;

    // Parse live parameter: explicit 1/0 wins, otherwise fall back to CP default
    const liveParam = params.get('live');
    state.live = liveParam === '1' ? true : liveParam === '0' ? false : defaultLive;
    if (liveParam === '1' || liveParam === '0') saveLiveChoice(liveParam === '1');

    state.sportSlug = params.get('sportSlug') ? params.get('sportSlug').split(',') : [];
    state.types = params.get('types') ? params.get('types').split(',') : [];
    state.skills = params.get('skills') ? params.get('skills').split(',') : [];
    state.gender = params.get('gender') ? params.get('gender').split(',') : [];

    // Normalize brands
    const brandParam = params.get('brand');
    state.brand = brandParam ? brandParam.split(',').map((b) => b.trim()) : [];

    // Brand page mode: typeBrand param indicates user arrived from a brand page
    const typeBrandParam = params.get('typeBrand');
    if (typeBrandParam) {
      state.brandPageMode = true;
      state.brandPageBrand = typeBrandParam.trim();
      state.brandPageBrandSelected = state.brand.length > 0;
      state.brandPageAllOthers = params.get('brandOthers') === '1';
      // Derive actual Algolia filters from both visual flags
      if (state.brandPageBrandSelected && !state.brandPageAllOthers) {
        // brand only → include filter
        state.brandExclude = [];
      } else if (!state.brandPageBrandSelected && state.brandPageAllOthers) {
        // all others only → exclude filter
        state.brand = [];
        state.brandExclude = [state.brandPageBrand];
      } else {
        // both or neither → no filter
        state.brand = [];
        state.brandExclude = [];
      }
    } else {
      state.brandPageMode = false;
      state.brandPageBrand = null;
      state.brandPageBrandSelected = false;
      state.brandPageAllOthers = false;
      state.brandExclude = [];
    }

    state.age = params.get('age') || '';

    // Gender filter checkboxes
    const genderFiltersParam = params.get('genderFilters');
    if (genderFiltersParam) {
      const genderFilters = genderFiltersParam.split(',').map((f) => f.trim());
      state.filterCoed = genderFilters.includes('coed');
      state.filterMaleOnly = genderFilters.includes('maleOnly');
      state.filterFemaleOnly = genderFilters.includes('femaleOnly');
    } else {
      state.filterCoed = false;
      state.filterMaleOnly = false;
      state.filterFemaleOnly = false;
    }

    state.sort = params.get('sort') || 'distance';

    // Decode tier parameter
    const tierParam = params.get('l');
    state.tier = tierParam
      ? tierParam.split(',').map((t) => decodeTier(t.trim())).filter(Boolean)
      : [];

    state.beginDates = params.get('beginDates') || '';
    state.endDates = params.get('endDates') || '';

    console.log('📋 Loaded state from URL parameters', state);
  } else {
    // No URL parameters - try to restore from localStorage
    const savedState = loadFromLocalStorage();
    if (savedState) {
      let zipValue = savedState.zip || '';
      if (!zipValue && window.CampsUtils?.getDefaultLocationString) {
        zipValue = window.CampsUtils.getDefaultLocationString() || '';
      }

      state.zip = zipValue;
      state.live = defaultLive;
      // Own detected location (empty/near-me OR the IP city) → center on IP coords.
      state.isDefaultIpLocation =
        window.CampsUtils?.isOwnDetectedLocation?.(state.zip) ?? false;
      
      state.sportSlug = savedState.sportSlug || [];
      state.types = savedState.types || [];
      state.skills = savedState.skills || [];
      state.gender = savedState.gender || [];
      state.brand = savedState.brand || [];
      state.age = savedState.age || '';
      state.sort = savedState.sort || 'distance';
      state.filterCoed = savedState.filterCoed || false;
      state.filterMaleOnly = savedState.filterMaleOnly || false;
      state.filterFemaleOnly = savedState.filterFemaleOnly || false;
      state.beginDates = savedState.beginDates || '';
      state.endDates = savedState.endDates || '';
      // Tier is URL-only by design (monolith parity): never restored from
      // localStorage. Keep it a safe array for restoreURLFromState.
      state.tier = [];

      console.log('📋 Restored state from localStorage');

      // Restore URL from state
      restoreURLFromState(params);
    } else {
      // No saved state - use defaults with default location for US users
      const defaultLocation = window.CampsUtils?.getDefaultLocationString?.() || '';
      state.zip = defaultLocation;
      // Own detected location (empty/near-me OR the IP city) → center on IP coords.
      state.isDefaultIpLocation =
        window.CampsUtils?.isOwnDetectedLocation?.(state.zip) ?? false;
      state.live = liveChoice ?? null;
      state.sportSlug = [];
      state.types = [];
      state.skills = [];
      state.gender = [];
      state.brand = [];
      state.age = '';
      state.sort = 'distance';
      state.tier = [];
      state.filterCoed = false;
      state.filterMaleOnly = false;
      state.filterFemaleOnly = false;
      state.beginDates = '';
      state.endDates = '';

      console.log('📋 Using default state with location:', defaultLocation);
    }
  }

  // Update UI from state
  syncUIFromState();

  // Initialize form components
  if (initializeFormComponents) {
    initializeFormComponents();
  }

  // Check for location metadata (state selection from autocomplete)
  const locationMeta = getAndClearLocationMeta();

  // Check if query is numeric (ZIP code) or matches known location (city/state)
  const isNumeric = /^\d+$/.test((state.zip || '').trim());
  const isKnownLoc = window.LocationAutocomplete?.isKnownLocation?.(state.zip) || false;

  // Fresh search dispatch (page load / popstate): clear stale dual-search state.
  // Camp-name paths re-set it; a stale ID from a previous in-page camp search
  // makes performSearch subtract 1 from the total (heading = nbHits - 1).
  state.foundCampObjectID = null;
  state.foundCampHit = null;

  if (locationMeta?.type === 'state' && (locationMeta.name || '').toLowerCase() === (state.zip || '').trim().toLowerCase()) {
    // State selected from autocomplete - skip geocoding
    handleStateSelection(locationMeta);
    toggleSkeleton(true);
    if (performSearch) performSearch();
  } else if (state.zip && (isNumeric || isKnownLoc)) {
    // Known location (ZIP, city, or state) - try getting coordinates from static data first
    console.log('📍 Known location detected:', state.zip, { isNumeric, isKnownLoc });

    // Try to get coordinates from static data (cities.json / zips.json) first
    const staticCoords =
      window.LocationAutocomplete?.getCoordinatesFromStatic?.(state.zip);

    if (staticCoords && staticCoords.lat && staticCoords.lng) {
      // Found in static data - use directly without geocoding
      console.log('✅ Using coordinates from static data:', staticCoords);
      // Auto-filled IP city (location not changed by the user): center on the
      // precise IP coordinates (realLocationData) instead of the static city
      // centroid, so the search center == the real location.
      const ipGeo = state.isDefaultIpLocation ? readUserGeoFromCache() : null;
      const useIpCoords = !!(
        ipGeo &&
        ipGeo.lat != null &&
        ipGeo.lng != null &&
        staticCoords.type !== 'state'
      );
      state.coordinates = useIpCoords
        ? { lat: ipGeo.lat, lng: ipGeo.lng }
        : { lat: staticCoords.lat, lng: staticCoords.lng };
      state.geocodeResult = {
        rawInput: state.zip,
        recognized: true,
        correctedLocationLabel: staticCoords.name || state.zip,
        // Populate coords + city/state so the decision-flow fallback can
        // rebuild the geo center (buildCenter) and the state-expand step.
        // Without these, a sport with 0 camps in radius hits a centerless
        // "loc" step that returns ALL camps ungeocoded (geoDistance=0 →
        // "0.0 mi away", unsorted) instead of falling through to the
        // distance-less "loc-global" step (matching a state search).
        correctedLat: staticCoords.lat,
        correctedLng: staticCoords.lng,
        correctedCity: staticCoords.type === 'city' ? staticCoords.city : '',
        correctedState:
          staticCoords.type === 'state'
            ? staticCoords.abbr
            : staticCoords.state || '',
        correctedRegion:
          staticCoords.type === 'state' ? staticCoords.name : '',
      };
      state.showCorrectedLocationBanner = false;
      state.showUnrecognizedLocationBanner = false;

      // State-level search from static data: set state search flags
      // so performSearch applies state filter and skips radius
      if (staticCoords.type === 'state' && staticCoords.abbr) {
        state.isStateSearch = true;
        state.searchStateCode = staticCoords.abbr;
        state.searchStateName = staticCoords.name || staticCoords.abbr;
        state.isExcludeUSSearch = false;
        state.excludeUSRegionLabel = null;
        state.regionFilter = null;
        state.ukStateValue = null;
        console.log('🏛️ State detected from static data:', {
          code: state.searchStateCode,
          name: state.searchStateName,
        });
      } else {
        state.isStateSearch = false;
        state.searchStateCode = null;
        state.searchStateName = null;
      }

      // Keep cache.coords as the SINGLE canonical "last search center", matching the
      // center this search actually uses (state.coordinates above) — so a later empty/
      // clear search (which reads cache.coords) re-centers on the SAME point and the
      // distance never jumps between operations. Source by case:
      //   • own detected city (useIpCoords) → write YOUR precise IP coords (realLocationData),
      //     NOT the static centroid, so coords == your real location (and overwrites any
      //     stale previously-searched city).
      //   • any other recognized location → write the static cities.json/zips.json coords.
      if (window.CampsUtils?.updateCachedLocationWithGeocode) {
        let geocodedData;
        if (useIpCoords) {
          let real = null;
          try {
            real =
              JSON.parse(
                localStorage.getItem(window.CampsUtils?.defaults?.cacheKey) || 'null'
              )?.realLocationData || null;
          } catch (e) {
            /* fall back to ipGeo fields below */
          }
          geocodedData = {
            lat: ipGeo.lat,
            lng: ipGeo.lng,
            city: real?.city || ipGeo.city || '',
            region: real?.region || '',
            state: real?.regionCode || ipGeo.state || '',
            postal: real?.postal || '',
            isUS: true,
          };
        } else {
          geocodedData = {
            lat: staticCoords.lat,
            lng: staticCoords.lng,
            city: staticCoords.city || '',
            region: staticCoords.name || '', // Full state name for states
            state: staticCoords.state || staticCoords.abbr || '', // 2-letter code
            postal: staticCoords.zip || '',
            isUS: true, // Static data is US-only
          };
        }
        window.CampsUtils.updateCachedLocationWithGeocode(geocodedData, state.zip);
      }

      toggleSkeleton(true);
      if (performSearch) performSearch();
    } else {
      // Not in static data or no coordinates - fall back to geocoding
      console.log('⚠️ Not found in static data, trying geocoding');
      await handleGeocoding();
    }
  } else if (state.zip) {
    // Unknown text - check if it looks like a camp name or a typo/misspelling
    if (isCampName(state.zip)) {
      // Query looks like a camp name (has keywords, long text, etc.)
      // Search for camp first to avoid Google extracting location words from camp names
      // (e.g., "Georgia" from "Bill Pilat's The Goalie School in Georgia For Girls")
      console.log(
        '🏕️ Query identified as camp name (length:',
        state.zip.length,
        '), searching camps first:',
        state.zip
      );
      await handleCampSearch();
    } else {
      // Query doesn't look like a camp name - might be a typo/misspelling of location
      // Try geocoding first to check for corrections (e.g., "carolna" → "North Carolina")
      // If Google returns a place (isPlace=true), fall back to camp search
      console.log(
        '🌍 Not a known location, trying geocoding first for typo correction:',
        state.zip
      );
      await handleUnknownLocationWithGeocodingFirst();
    }
  } else {
    // No location - use IP coordinates as fallback
    handleNoLocation();
    if (performSearch) performSearch();
  }
}

/**
 * Restore URL from current state (when loading from localStorage)
 */
function restoreURLFromState(currentParams) {
  // Start from the current query string: it holds no search keys here, only
  // ad/tracking params (gclid, utm_*, fbclid...), which must stay in the URL.
  const tempParams = new URLSearchParams(currentParams);

  if (state.zip && state.zip.trim()) tempParams.set('q', state.zip);
  if (state.beginDates && state.endDates) {
    tempParams.set('beginDates', state.beginDates);
    tempParams.set('endDates', state.endDates);
  }
  if (state.sportSlug.length > 0) tempParams.set('sportSlug', state.sportSlug.join(','));
  if (state.skills.length > 0) tempParams.set('skills', state.skills.join(','));
  if (state.gender.length > 0) tempParams.set('gender', state.gender.join(','));
  if (state.age) tempParams.set('age', state.age);
  if (state.brand.length > 0) tempParams.set('brand', state.brand.join(','));
  if (state.tier && state.tier.length > 0) tempParams.set('l', state.tier.join(','));

  const tempParamsString = tempParams.toString();
  if (tempParamsString !== currentParams.toString()) {
    const tempURL = window.location.pathname + '?' + tempParamsString;
    const currentURL = window.location.pathname + window.location.search;
    if (currentURL !== tempURL) {
      window.history.replaceState({}, '', tempURL);
    }

    // Re-initialize form components from URL
    if (window.DetailsSelection?.initFromURL) {
      window.DetailsSelection.initFromURL();
    }
  }
}

/**
 * Sync UI inputs from state
 */
export function syncUIFromState() {
  if (elements.zipInput) {
    elements.zipInput.value = state.zip;
    elements.zipInput.dispatchEvent(new Event('input', { bubbles: true }));
    // Update placeholder visibility
    if (window.LocationAutocomplete?.updatePlaceholderVisibility) {
      window.LocationAutocomplete.updatePlaceholderVisibility(elements.zipInput);
    }
  }
  if (elements.mobileZipInput) {
    elements.mobileZipInput.value = state.zip;
    elements.mobileZipInput.dispatchEvent(new Event('input', { bubbles: true }));
    // Update placeholder visibility
    if (window.LocationAutocomplete?.updatePlaceholderVisibility) {
      window.LocationAutocomplete.updatePlaceholderVisibility(elements.mobileZipInput);
    }
  }
  // Live checkbox is NOT synced here (monolith parity): runExceptions may flip
  // state.live after this runs; updateFilterUI syncs the checkbox after every search.
  if (elements.ageInput) elements.ageInput.value = state.age;
  if (elements.sortSelect) elements.sortSelect.value = state.sort;

  // Update type-filter select
  if (elements.typeFilterSelect) {
    if (['distance', 'title-asc', 'title-desc'].includes(state.sort)) {
      elements.typeFilterSelect.value = 'distance';
    } else if (state.sort === 'date-asc') {
      // Main template option is "date-asc", embedded template's is "date"
      elements.typeFilterSelect.value = 'date-asc';
      if (elements.typeFilterSelect.selectedIndex === -1) {
        elements.typeFilterSelect.value = 'date';
      }
    } else if (state.sort === 'popularity') {
      elements.typeFilterSelect.value = 'popularity';
    } else {
      elements.typeFilterSelect.value = 'distance';
    }
  }
}

/**
 * Get and clear location metadata from localStorage
 */
function getAndClearLocationMeta() {
  try {
    const metaData = localStorage.getItem('locationMeta');
    if (metaData) {
      localStorage.removeItem('locationMeta');
      return JSON.parse(metaData);
    }
  } catch (e) {
    console.warn('Failed to read location metadata:', e);
  }
  return null;
}

/**
 * Handle state selection from autocomplete
 */
function handleStateSelection(locationMeta) {
  console.log('🏛️ State selected from autocomplete:', locationMeta);
  state.isStateSearch = true;
  state.searchStateCode = locationMeta.code;
  state.searchStateName = locationMeta.name;
  state.coordinates = { lat: locationMeta.lat, lng: locationMeta.lng };
  state.isExcludeUSSearch = false;
  state.excludeUSRegionLabel = null;
  state.regionFilter = null;
  state.ukStateValue = null;
  state.ukCountryFilter = null;
  state.geocodeResult = {
    rawInput: state.zip,
    recognized: true,
    correctedLocationLabel: locationMeta.name,
  };
  state.showCorrectedLocationBanner = false;
  state.showUnrecognizedLocationBanner = false;

  // State selection bypasses the geo cache write — log the overwrite
  // (state centroid) explicitly for the session location log
  window.CampsUtils?.logLocationEvent?.({
    source: 'search',
    fromCache: false,
    lat: locationMeta.lat,
    lng: locationMeta.lng,
    region: locationMeta.name,
    country: 'US',
  });
}

/**
 * Handle unknown location with geocoding-first approach
 * For queries that don't look like camp names, try geocoding first to check for typos/corrections
 * (e.g., "carolna" → "North Carolina")
 * If geocoding returns a place (isPlace=true), fall back to camp search
 */
async function handleUnknownLocationWithGeocodingFirst() {
  toggleSkeleton(true);

  // Reset state flags
  state.isStateSearch = false;
  state.searchStateCode = null;
  state.searchStateName = null;
  state.isExcludeUSSearch = false;
  state.excludeUSRegionLabel = null;
  state.regionFilter = null;
  state.ukStateValue = null;
  state.ukCountryFilter = null;

  try {
    if (!window.CampsUtils?.geocodeZip) {
      console.warn('CampsUtils.geocodeZip not available, trying camp search');
      await handleCampSearch();
      return;
    }

    const geocodedData = await window.CampsUtils.geocodeZip(state.zip);

    // Check if geocoding returned a valid location (not a place/establishment)
    if (
      geocodedData &&
      !geocodedData.isPlace &&
      !geocodedData.excludeUS &&
      geocodedData.lat &&
      geocodedData.lng
    ) {
      // Valid US location found (e.g., typo "carolna" corrected to "North Carolina")
      console.log(
        '✅ Geocoding succeeded with valid location (not a place):',
        geocodedData
      );
      console.log('   → Using geocoding result instead of camp search');

      // Use geocoding result - handle as successful geocode
      await handleSuccessfulGeocode(geocodedData);
    } else if (geocodedData && geocodedData.excludeUS) {
      // Non-US location (Europe, UK, Canada, etc.) - use existing excludeUS flow
      console.log('🌍 Geocoding returned non-US location, using excludeUS flow');
      handleEuropeUKGeocode(geocodedData);
      toggleSkeleton(true);
      if (performSearch) performSearch();
    } else if (geocodedData && geocodedData.isPlace) {
      // Geocoding returned a place (camp/business name), not a location
      console.log(
        '⚠️ Geocoding returned a place/establishment (not a location), falling back to camp search'
      );
      console.log(
        '   → isPlace:',
        geocodedData.isPlace,
        'containsPlaceKeyword:',
        geocodedData.containsPlaceKeyword
      );
      await handleCampSearch();
    } else {
      // Geocoding failed - try camp search
      console.log(
        '⚠️ Geocoding failed or returned null, falling back to camp search'
      );
      await handleCampSearch();
    }
  } catch (error) {
    console.error('❌ Geocoding error:', error);
    await handleCampSearch();
  }
}

/**
 * Handle camp search for text queries
 * First tries to find camp by title, then falls back to geocoding if not found
 * This prevents Google from extracting location words from camp names
 * (e.g., "Georgia" from "Bill Pilat's The Goalie School in Georgia For Girls")
 */
async function handleCampSearch() {
  toggleSkeleton(true);

  // Reset state flags
  state.isStateSearch = false;
  state.searchStateCode = null;
  state.searchStateName = null;
  state.isExcludeUSSearch = false;
  state.excludeUSRegionLabel = null;
  state.regionFilter = null;
  state.ukStateValue = null;
  state.ukCountryFilter = null;

  try {
    // STEP 1: Search by title WITHOUT filters to detect specific camp match
    // This ensures a specific camp (e.g. lacrosse) is found even if sport filter is different (e.g. football)
    const unfilteredResult = await searchCampByTitle(state.zip, {
      sportSlug: [],
      applyAllFilters: false,
    });

    if (unfilteredResult?.hits?.length === 1) {
      // SINGLE CAMP FOUND → dual search (camp match + nearby with filters)
      console.log('🎯 Single camp match (unfiltered) - using dual search flow');
      await handleCampFound(unfilteredResult);
    } else {
      // NOT a single camp match → STEP 2: search WITH all filters
      console.log(
        '🔍 Not a single camp match (unfiltered:',
        unfilteredResult?.hits?.length || 0,
        'results) → searching with filters'
      );

      const filteredResult = await searchCampByTitle(state.zip, {
        sportSlug: state.sportSlug,
        applyAllFilters: true,
      });

      if (filteredResult?.hits?.length > 0) {
        // Camp(s) found with filters - branch based on result count
        console.log(`✅ Found ${filteredResult.hits.length} camps by title search with filters`);
        await handleCampFound(filteredResult);
      } else {
        // No camp found by title - show unrecognized location
        console.log('⚠️ No camps found by title, location unrecognized');
        state.geocodeResult = { rawInput: state.zip, recognized: false };
        state.showUnrecognizedLocationBanner = true;
        state.unrecognizedLocationBannerContent = null;
        applyCoordinatesFromCache();
        toggleSkeleton(true);
        runDecisionFlow();
      }
    }
  } catch (error) {
    console.error('❌ Error searching for camp by title:', error);
    state.geocodeResult = { rawInput: state.zip, recognized: false };
    state.showUnrecognizedLocationBanner = true;
    state.unrecognizedLocationBannerContent = null;
    applyCoordinatesFromCache();
    toggleSkeleton(true);
    runDecisionFlow();
  }
}

/**
 * Handle no location provided
 */
function handleNoLocation() {
  state.isStateSearch = false;
  state.searchStateCode = null;
  state.searchStateName = null;
  state.isExcludeUSSearch = false;
  state.excludeUSRegionLabel = null;
  state.regionFilter = null;
  state.ukStateValue = null;
  state.ukCountryFilter = null;
  if (!state.geocodeResult) {
    state.geocodeResult = { rawInput: '', recognized: false };
  }
  if (!state.coordinates) {
    applyCoordinatesFromCache();
  }
}

/**
 * Handle geocoding flow
 * Massive function that handles all geocoding scenarios
 */
async function handleGeocoding() {
  const geocodedData = await window.CampsUtils.geocodeZip(state.zip);

  // Europe/UK region
  if (geocodedData?.excludeUS) {
    handleEuropeUKGeocode(geocodedData);
    toggleSkeleton(true);
    if (performSearch) performSearch();
    return;
  }

  // Successfully geocoded
  if (geocodedData?.lat && geocodedData?.lng) {
    await handleSuccessfulGeocode(geocodedData);
    return;
  }

  // Geocoding failed - try camp search
  await handleFailedGeocode();
}

/**
 * Handle Europe/UK geocode result
 */
function handleEuropeUKGeocode(geocodedData) {
  state.isExcludeUSSearch = true;
  state.excludeUSRegionLabel = geocodedData.regionLabel || 'Europe';
  state.regionFilter = geocodedData.regionFilter || null;
  state.ukStateValue = geocodedData.stateValue || null;
  state.ukCountryFilter = geocodedData.ukCountryFilter || null;
  state.sortByDistanceNoRadius = geocodedData.sortByDistanceNoRadius || false;
  state.isStateSearch = false;
  state.searchStateCode = null;
  state.searchStateName = null;

  if (geocodedData.lat != null && geocodedData.lng != null) {
    state.coordinates = { lat: geocodedData.lat, lng: geocodedData.lng };
  } else {
    state.coordinates = null;
  }

  state.geocodeResult = {
    rawInput: state.zip,
    recognized: true,
    excludeUS: true,
    correctedLocationLabel: geocodedData.regionLabel || 'Europe',
  };
  state.showCorrectedLocationBanner = false;
  state.showUnrecognizedLocationBanner = false;
}

/**
 * Handle successful geocode result
 */
async function handleSuccessfulGeocode(geocodedData) {
  state.isExcludeUSSearch = false;
  state.excludeUSRegionLabel = null;
  state.regionFilter = null;
  state.ukStateValue = null;
  state.ukCountryFilter = null;
  state.coordinates = { lat: geocodedData.lat, lng: geocodedData.lng };

  // Determine corrected label and state
  let correctedState = geocodedData.state || geocodedData.regionCode || '';
  if (!correctedState && geocodedData.region) {
    const region = String(geocodedData.region).trim();
    if (/^[A-Za-z]{2}$/.test(region)) {
      correctedState = region.toUpperCase();
    }
  }

  // Build corrected label
  const userMainPart = (state.zip || '').split(',')[0].trim();
  const cityLower = (geocodedData.city || '').toLowerCase();
  const userMainPartLower = userMainPart.toLowerCase();

  const inputMatchesCity =
    cityLower &&
    userMainPartLower &&
    (normalizeCompareStr(cityLower).includes(normalizeCompareStr(userMainPartLower)) ||
      normalizeCompareStr(userMainPartLower).includes(normalizeCompareStr(cityLower)));

  let correctedLabel;
  if (
    !inputMatchesCity &&
    geocodedData.city &&
    (geocodedData.neighborhood || geocodedData.sublocality) &&
    correctedState
  ) {
    correctedLabel =
      state.zip && state.zip.includes(correctedState)
        ? state.zip
        : `${userMainPart}, ${correctedState}`;
  } else {
    correctedLabel =
      geocodedData.city && correctedState
        ? `${geocodedData.city}, ${correctedState}`
        : geocodedData.city || geocodedData.region || correctedState || state.zip;
  }

  const isZip = isZipCode(state.zip);
  const cityMatches =
    geocodedData.city && state.zip
      ? isLocationEffectivelySame(state.zip, geocodedData.city) ||
        normalizeCompareStr(state.zip).includes(normalizeCompareStr(geocodedData.city)) ||
        normalizeCompareStr(geocodedData.city).includes(normalizeCompareStr(state.zip))
      : false;

  const hasTypos =
    !isZip && state.zip && correctedLabel && !cityMatches
      ? hasSpellingMistakes(state.zip, correctedLabel, geocodedData.city)
      : false;

  const allGeocodeParts = [
    geocodedData.formatted_address,
    geocodedData.city,
    geocodedData.sublocality,
    geocodedData.neighborhood,
  ]
    .filter(Boolean)
    .map((s) => String(s))
    .join(' ');

  const inputIsPartOfResult =
    userMainPart.length >= 3 &&
    normalizeCompareStr(allGeocodeParts).includes(normalizeCompareStr(userMainPart));

  const textDiffers =
    !isZip &&
    state.zip &&
    correctedLabel &&
    !cityMatches &&
    !inputIsPartOfResult &&
    (hasTypos || !isLocationEffectivelySame(state.zip, correctedLabel));

  state.showCorrectedLocationBanner = textDiffers;

  state.geocodeResult = {
    rawInput: state.zip,
    recognized: true,
    correctedLocationLabel: correctedLabel,
    correctedCity: geocodedData.city || '',
    correctedState: correctedState,
    correctedRegion: geocodedData.region || '',
    correctedLat: geocodedData.lat,
    correctedLng: geocodedData.lng,
    cityMatches: cityMatches,
    inputIsPartOfResult: inputIsPartOfResult,
  };

  state.showUnrecognizedLocationBanner = false;
  state.unrecognizedLocationBannerContent = null;

  // State-only search
  const isStateInput = !(geocodedData.city || '').trim();
  if (isStateInput && (correctedState || geocodedData.region)) {
    state.isStateSearch = true;
    state.searchStateCode =
      correctedState ||
      (window.CampsUtils?.mapStateNameToCode?.(geocodedData.region) || geocodedData.region);
    state.searchStateName = geocodedData.region || correctedState || state.searchStateCode;
  } else {
    state.isStateSearch = false;
    state.searchStateCode = null;
    state.searchStateName = null;
  }

  // Update cache
  if (window.CampsUtils?.updateCachedLocationWithGeocode) {
    window.CampsUtils.updateCachedLocationWithGeocode(geocodedData, state.zip);
  }

  // If Google returned a place, search for camp by title
  if (geocodedData.isPlace) {
    await handlePlaceSearch(hasTypos);
  } else {
    // Regular location
    if (hasTypos) {
      toggleSkeleton(true);
      runDecisionFlow();
    } else {
      // Reset dual search state - this is a new geo search (monolith parity);
      // a stale foundCampObjectID makes performSearch subtract 1 from the total
      state.foundCampObjectID = null;
      state.foundCampHit = null;
      if (performSearch) performSearch();
    }
  }
}

/**
 * Handle place search (camp name)
 */
async function handlePlaceSearch(hasTypos) {
  console.log('📍 Google API returned place, searching for camp:', state.zip);
  toggleSkeleton(true);

  try {
    const campSearchResult = await searchCampByTitle(state.zip);

    if (campSearchResult?.hits?.length > 0) {
      // Camp found - apply filters and render
      await handleCampFound(campSearchResult);
    } else {
      // Camp not found
      state.geocodeResult.isPlace = true;
      state.geocodeResult.isCampFound = false;
      state.isStateSearch = false;

      if (hasTypos) {
        runDecisionFlow();
      } else {
        if (performSearch) performSearch();
      }
    }
  } catch (error) {
    console.error('❌ Error searching for camp:', error);
    state.geocodeResult.isPlace = true;
    state.geocodeResult.isCampFound = false;

    if (hasTypos) {
      runDecisionFlow();
    } else {
      if (performSearch) performSearch();
    }
  }
}

/**
 * Handle camp found by title search
 * Branches based on result count:
 * - 1 result: use dual search flow (camp match + nearby camps)
 * - Multiple results: render as regular search results
 */
async function handleCampFound(campSearchResult) {
  const hitCount = campSearchResult.hits.length;
  console.log(`✅ Found ${hitCount} camps by title search with filters`);

  // BRANCHING LOGIC: 1 result → dual search, multiple → regular results
  if (hitCount === 1) {
    // SINGLE RESULT → Use dual search flow (camp match + nearby camps)
    console.log('🎯 Single camp match - using dual search flow');

    if (campSearchResult.firstCamp) {
      state.coordinates = {
        lat: campSearchResult.firstCamp.lat,
        lng: campSearchResult.firstCamp.lng,
      };
    }

    state.geocodeResult = {
      rawInput: state.zip,
      recognized: true,
      correctedLocationLabel: state.zip,
      correctedCity: campSearchResult.firstCamp?.city || '',
      correctedState: campSearchResult.firstCamp?.state || '',
      correctedRegion: campSearchResult.firstCamp?.state || '',
      correctedLat: campSearchResult.firstCamp?.lat || null,
      correctedLng: campSearchResult.firstCamp?.lng || null,
      isCampFound: true,
    };

    state.showCorrectedLocationBanner = false;
    state.showUnrecognizedLocationBanner = false;

    // Update cache with camp's location
    if (
      window.CampsUtils?.updateCachedLocationWithGeocode &&
      campSearchResult.firstCamp?.lat &&
      campSearchResult.firstCamp?.lng
    ) {
      const campCity = campSearchResult.firstCamp.city || '';
      const campState = campSearchResult.firstCamp.state || '';
      const userLocationForCache =
        campCity && campState
          ? `${campCity}, ${campState}`
          : campCity || campState || '';

      if (userLocationForCache) {
        window.CampsUtils.updateCachedLocationWithGeocode(
          {
            lat: campSearchResult.firstCamp.lat,
            lng: campSearchResult.firstCamp.lng,
            city: campCity,
            region: campState,
            state: campState,
            postal: '',
            isUS: true,
          },
          userLocationForCache
        );
        console.log('✅ Cache updated with camp location:', userLocationForCache);
      }
    }

    // Use dual search for single result
    try {
      await performDualSearch(state.zip);
    } catch (error) {
      console.error('❌ Dual search failed:', error);
      runDecisionFlow();
    }
  } else {
    // MULTIPLE RESULTS → Render as regular search results (no "camp match" block)
    console.log(`📋 Multiple camp matches (${hitCount}) - rendering as regular results`);

    if (campSearchResult.firstCamp) {
      state.coordinates = {
        lat: campSearchResult.firstCamp.lat,
        lng: campSearchResult.firstCamp.lng,
      };
    }

    // Mark as title search (NOT isCampFound - that's for dual search)
    state.geocodeResult = {
      rawInput: state.zip,
      recognized: true,
      isTitleSearch: true, // Flag for renderResults heading
    };

    // Clear dual search state
    state.foundCampObjectID = null;
    state.dualSearchLocation = null;
    state.implicitSportSlug = null;

    state.showCorrectedLocationBanner = false;
    state.showUnrecognizedLocationBanner = false;

    // Results already filtered by searchCampByTitle with applyAllFilters: true
    // Just sort and render
    let hits = sortHits(campSearchResult.hits);

    // Store results
    const totalCount = campSearchResult.nbHits || hits.length;
    state.allFilteredHits = hits;
    state.totalHits = totalCount;
    state.displayedHits = state.hitsPerPage;

    // Update facets from results
    updateFacetsFromHits(hits);

    // Render results
    toggleSkeleton(false);
    hideFallbackBanner();

    const hitsToDisplay = hits.slice(0, state.displayedHits);

    const { renderResults } = await import('../ui/results-renderer.js');
    if (renderResults) {
      renderResults(hitsToDisplay, totalCount, false, null);
    }
    const { updateURL: updateURLFn } = await import('./url-updater.js');
    if (updateURLFn) updateURLFn();
    const { updateFilterUI: updateFilterUIFn, updateFiltersCount: updateFiltersCountFn } =
      await import('../ui/filter-ui.js');
    if (updateFilterUIFn) updateFilterUIFn();
    // Title-search render bypasses performSearch — refresh "Filters (N)" here too
    if (updateFiltersCountFn) updateFiltersCountFn();

    console.log('✅ [TITLE SEARCH] Rendered', hitsToDisplay.length, 'of', totalCount, 'camps (total from Algolia)');
  }
}

/**
 * Update facets from hits array (for filter UI)
 * Used when results come from title search without facets from Algolia
 * @param {Array} hits - Array of camp hits
 */
function updateFacetsFromHits(hits) {
  const facets = {
    sportSlug: {},
    types: {},
    gender: {},
    brands: {},
    skills: {},
    live: {},
  };

  hits.forEach((hit) => {
    if (hit.sportSlug) {
      facets.sportSlug[hit.sportSlug] = (facets.sportSlug[hit.sportSlug] || 0) + 1;
    }
    if (hit.feeCodes && Array.isArray(hit.feeCodes)) {
      hit.feeCodes.forEach((code) => {
        facets.types[code] = (facets.types[code] || 0) + 1;
      });
    }
    if (hit.gender && Array.isArray(hit.gender)) {
      hit.gender.forEach((g) => {
        facets.gender[g] = (facets.gender[g] || 0) + 1;
      });
    }
    if (hit.brands && Array.isArray(hit.brands)) {
      hit.brands.forEach((b) => {
        facets.brands[b] = (facets.brands[b] || 0) + 1;
      });
    }
    if (hit.skills && Array.isArray(hit.skills)) {
      hit.skills.forEach((s) => {
        facets.skills[s] = (facets.skills[s] || 0) + 1;
      });
    }
    if (hit.live !== undefined) {
      const liveKey = String(hit.live);
      facets.live[liveKey] = (facets.live[liveKey] || 0) + 1;
    }
  });

  state.facets = facets;

  // Save as allFacets baseline so filter options don't disappear when filters change
  state.allFacets.feeCodes = mergeFacetKeys(
    state.allFacets.feeCodes || {},
    facets.types || {}
  );
  state.allFacets.brands = mergeFacetKeys(
    state.allFacets.brands || {},
    facets.brands || {}
  );
  state.allFacets.skills = mergeFacetKeys(
    state.allFacets.skills || {},
    facets.skills || {}
  );

  console.log('📊 [TITLE SEARCH] Updated facets from hits:', facets);
}

/**
 * Apply client-side filters to hits
 */
function applyClientSideFilters(hits) {
  let filtered = hits;

  // Sport filter
  if (state.sportSlug.length > 0) {
    filtered = filtered.filter((camp) => state.sportSlug.some((slug) => camp.sportSlug === slug));
  }

  // Types filter
  if (state.types.length > 0) {
    filtered = filtered.filter(
      (camp) =>
        state.types.some((type) => camp.feeCodes && camp.feeCodes.includes(type)) ||
        (camp.feeCodes && camp.feeCodes.includes('_empty'))
    );
  }

  // Gender filter
  const genderExpr = buildGenderFilterExpression();
  if (genderExpr) {
    const selectedGender = getSelectedGender();
    if (selectedGender === 'male') {
      filtered = filtered.filter(
        (camp) => camp.gender && (camp.gender.includes('male') || camp.gender.includes('all'))
      );
    } else if (selectedGender === 'female') {
      filtered = filtered.filter(
        (camp) => camp.gender && (camp.gender.includes('female') || camp.gender.includes('all'))
      );
    }

    if (state.filterCoed) {
      filtered = filtered.filter((camp) => camp.gender && camp.gender.includes('all'));
    }
    if (state.filterMaleOnly) {
      filtered = filtered.filter(
        (camp) => camp.gender && camp.gender.includes('male') && !camp.gender.includes('all')
      );
    }
    if (state.filterFemaleOnly) {
      filtered = filtered.filter(
        (camp) => camp.gender && camp.gender.includes('female') && !camp.gender.includes('all')
      );
    }
  }

  // Brand filter
  if (state.brand.length > 0) {
    filtered = filtered.filter((camp) => state.brand.some((brand) => camp.brand === brand));
  }

  // Live filter
  if (state.live === true) {
    filtered = filtered.filter((camp) => camp.live === true);
  }

  // Tier filter
  if (state.tier.length > 0) {
    filtered = filtered.filter((camp) => state.tier.includes(String(camp.tier || 0)));
  }

  // Age filter
  if (state.age) {
    if (state.age === '22+') {
      filtered = filtered.filter(
        (camp) => (camp.minAge && camp.minAge >= 22) || (camp.maxAge && camp.maxAge >= 22)
      );
    } else {
      const age = parseInt(state.age);
      if (!isNaN(age)) {
        filtered = filtered.filter((camp) => camp.minAge <= age && camp.maxAge >= age);
      }
    }
  }

  // Date filter
  if (state.beginDates && state.endDates) {
    const dateRange = getDateRangeTimestamps();
    if (dateRange) {
      filtered = filtered.filter((camp) => campMatchesDateFilter(camp, dateRange));
    }
  }

  return filtered;
}

/**
 * Sort hits based on state.sort
 */
function sortHits(hits) {
  if (state.sort === 'title-asc') {
    hits.sort((a, b) =>
      (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase())
    );
  } else if (state.sort === 'title-desc') {
    hits.sort((a, b) =>
      (b.title || '').toLowerCase().localeCompare((a.title || '').toLowerCase())
    );
  } else if (state.sort === 'date-asc') {
    hits.sort((a, b) => {
      const dateA = getCampStartDate(a);
      const dateB = getCampStartDate(b);
      if (!dateA && !dateB) {
        return (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
      }
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA - dateB;
    });
  }
  return hits;
}

/**
 * Handle failed geocode
 */
async function handleFailedGeocode() {
  console.warn('⚠️ Geocoding failed for location:', state.zip);
  state.isStateSearch = false;
  state.searchStateCode = null;
  state.searchStateName = null;

  try {
    const campSearchResult = await searchCampByTitle(state.zip);

    if (campSearchResult?.lat && campSearchResult?.lng) {
      // Camp found
      state.coordinates = { lat: campSearchResult.lat, lng: campSearchResult.lng };

      const correctedState = campSearchResult.state || '';
      const correctedLabel =
        campSearchResult.city && correctedState
          ? `${campSearchResult.city}, ${correctedState}`
          : campSearchResult.city || correctedState || state.zip;

      state.geocodeResult = {
        rawInput: state.zip,
        recognized: true,
        correctedLocationLabel: correctedLabel,
        correctedCity: campSearchResult.city || '',
        correctedState: correctedState,
        correctedRegion: campSearchResult.state || '',
        correctedLat: campSearchResult.lat,
        correctedLng: campSearchResult.lng,
      };

      state.showUnrecognizedLocationBanner = false;
      state.unrecognizedLocationBannerContent = null;

      toggleSkeleton(true);
      if (performSearch) performSearch();
    } else {
      // No camp found - use fallback
      handleUnrecognizedLocation();
    }
  } catch (error) {
    console.error('❌ Error searching for camp:', error);
    handleUnrecognizedLocation();
  }
}

/**
 * Handle unrecognized location
 */
function handleUnrecognizedLocation() {
  state.isStateSearch = false;
  state.searchStateCode = null;
  state.searchStateName = null;
  state.geocodeResult = { rawInput: state.zip, recognized: false };
  state.showUnrecognizedLocationBanner = true;
  state.unrecognizedLocationBannerContent = null;

  if (!state.coordinates) {
    applyCoordinatesFromCache();
  }

  toggleSkeleton(true);
  runDecisionFlow();
}

console.log('📦 URL parser module loaded');
